import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Truck, Phone, Mail, Building2, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'freight_forwarder', label: 'Freight Forwarder' },
  { value: 'cha', label: 'CHA (Customs House Agent)' },
  { value: 'insurance', label: 'Insurance Provider' },
  { value: 'logistics', label: 'Logistics Provider' },
  { value: 'other', label: 'Other' }
];

export default function ServiceProviderMasterPage() {
  const { getAuthHeader } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [form, setForm] = useState({
    name: '',
    category: 'freight_forwarder',
    gstin: '',
    pan: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    status: 'active'
  });

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await axios.get(`${API_URL}/service-providers?${params}`, { 
        headers: getAuthHeader() 
      });
      setProviders(response.data.providers || []);
    } catch (error) {
      toast.error('Failed to fetch service providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [categoryFilter]);

  const resetForm = () => {
    setForm({
      name: '',
      category: 'freight_forwarder',
      gstin: '',
      pan: '',
      bank_name: '',
      bank_account: '',
      bank_ifsc: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      status: 'active'
    });
    setEditProvider(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name) {
      toast.error('Provider name is required');
      return;
    }

    try {
      if (editProvider) {
        await axios.patch(`${API_URL}/service-providers/${editProvider.id}`, form, { 
          headers: getAuthHeader() 
        });
        toast.success('Service provider updated');
      } else {
        await axios.post(`${API_URL}/service-providers`, form, { 
          headers: getAuthHeader() 
        });
        toast.success('Service provider created');
      }
      setOpen(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save service provider';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      toast.error(errorMessage);
    }
  };

  const handleEdit = (provider) => {
    setEditProvider(provider);
    setForm({
      name: provider.name || '',
      category: provider.category || 'freight_forwarder',
      gstin: provider.gstin || '',
      pan: provider.pan || '',
      bank_name: provider.bank_name || '',
      bank_account: provider.bank_account || '',
      bank_ifsc: provider.bank_ifsc || '',
      contact_person: provider.contact_person || '',
      phone: provider.phone || '',
      email: provider.email || '',
      address: provider.address || '',
      status: provider.status || 'active'
    });
    setOpen(true);
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`Delete ${provider.name}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/service-providers/${provider.id}`, { 
        headers: getAuthHeader() 
      });
      toast.success('Service provider deleted');
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getCategoryLabel = (value) => {
    const cat = CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const getCategoryColor = (category) => {
    const colors = {
      freight_forwarder: 'bg-blue-100 text-blue-800',
      cha: 'bg-purple-100 text-purple-800',
      insurance: 'bg-green-100 text-green-800',
      logistics: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const filteredProviders = providers.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.gstin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Summary stats
  const activeCount = providers.filter(p => p.status === 'active').length;
  const byCategoryCount = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = providers.filter(p => p.category === cat.value).length;
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="service-provider-master-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Service Provider Master</h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage freight forwarders, CHAs, insurance providers
            </p>
          </div>
          <Button 
            onClick={() => { resetForm(); setOpen(true); }}
            data-testid="add-provider-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Provider
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total</div>
              <div className="text-2xl font-bold">{providers.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{activeCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Freight Forwarders</div>
              <div className="text-2xl font-bold text-blue-600">{byCategoryCount.freight_forwarder || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">CHA</div>
              <div className="text-2xl font-bold text-purple-600">{byCategoryCount.cha || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Insurance</div>
              <div className="text-2xl font-bold text-green-600">{byCategoryCount.insurance || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, GSTIN, contact..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredProviders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No service providers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProviders.map(provider => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <div className="font-medium">{provider.name}</div>
                        {provider.address && (
                          <div className="text-xs text-gray-500">{provider.address}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getCategoryColor(provider.category)}>
                          {getCategoryLabel(provider.category)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>{provider.gstin || '-'}</div>
                        {provider.pan && (
                          <div className="text-xs text-gray-500">PAN: {provider.pan}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {provider.contact_person && (
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {provider.contact_person}
                            </div>
                          )}
                          {provider.phone && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <Phone className="h-3 w-3" />
                              {provider.phone}
                            </div>
                          )}
                          {provider.email && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <Mail className="h-3 w-3" />
                              {provider.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={provider.status === 'active' ? 'default' : 'secondary'}>
                          {provider.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(provider)}
                          data-testid={`edit-provider-${provider.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(provider)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-provider-${provider.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editProvider ? 'Edit Service Provider' : 'Add Service Provider'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Provider Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ABC Freight Forwarders"
                    data-testid="provider-name-input"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select 
                    value={form.category} 
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger data-testid="provider-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={form.status} 
                    onValueChange={(v) => setForm({ ...form, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>GSTIN</Label>
                  <Input
                    value={form.gstin}
                    onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                    placeholder="27AAACC4175D1ZE"
                  />
                </div>
                <div>
                  <Label>PAN</Label>
                  <Input
                    value={form.pan}
                    onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                    placeholder="AAACC4175D"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Bank Details</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      placeholder="HDFC Bank"
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={form.bank_account}
                      onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                      placeholder="50200012345678"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={form.bank_ifsc}
                      onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })}
                      placeholder="HDFC0001234"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Contact Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Person</Label>
                    <Input
                      value={form.contact_person}
                      onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="9876543210"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="contact@abc.com"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="123 Shipping Lane, Mumbai"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-provider-btn">
                  {editProvider ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
